import {
  Modal,
  Text,
  Group,
  Box,
  Stack,
  Button,
  ActionIcon,
} from '@mantine/core';
import { Calendar as CalendarIcon, Check, Clock, User, X } from 'react-feather';
import {
  ChangeOrCancel,
  InviteOnItsWay,
} from '../../../components/scheduling/InviteSentNotice.js';
import { getTimezoneShortName } from '../../../components/scheduling/slotPickerUtils.js';

export interface InviteSentModalProps {
  opened: boolean;
  /** Closes the modal. The caller refreshes the dashboard here. */
  onDone: () => void;
  /** For example "Check-in booked" or "Session rescheduled". */
  heading: string;
  sessionTitle: string;
  /** The booked start, an ISO time. */
  startTime: string;
  dateText: string;
  timeText: string;
  durationMinutes: number;
  timezone: string;
  coachName: string;
  email: string;
  /**
   * True when Medley sent a Google Calendar invite (calendly_fallback). False
   * when Calendly booked it and sends its own invite.
   */
  fallback: boolean;
}

/**
 * MED-1365: the confirmation after a portal booking or reschedule. The same
 * words, badge and zone label as the public booking page, with the portal's
 * change and cancel line.
 */
export function InviteSentModal({
  opened,
  onDone,
  heading,
  sessionTitle,
  startTime,
  dateText,
  timeText,
  durationMinutes,
  timezone,
  coachName,
  email,
  fallback,
}: InviteSentModalProps) {
  return (
    <Modal
      opened={opened}
      onClose={onDone}
      size="md"
      radius="lg"
      withCloseButton={false}
      padding={0}
    >
      <Group justify="flex-end" px="lg" pt="md">
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onDone}
          size="sm"
          aria-label="Close"
        >
          <X size={16} />
        </ActionIcon>
      </Group>

      <Stack gap="md" px="lg" pb="lg" align="center">
        <Box
          aria-hidden
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: 'var(--medley-bg-success)',
            color: 'var(--medley-green-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Check size={28} />
        </Box>
        <Text fw={700} size="lg" ta="center">
          {heading}
        </Text>

        <Box
          w="100%"
          p="md"
          style={{
            backgroundColor: 'var(--medley-surface-light)',
            borderRadius: 8,
            border: '1px solid var(--medley-border)',
          }}
        >
          <Text fw={600} size="sm" mb="xs">
            {sessionTitle}
          </Text>
          <Stack gap={6}>
            <Group gap={8} wrap="nowrap">
              <CalendarIcon size={14} color="var(--medley-text-secondary)" />
              <Text size="sm" c="dimmed">
                {dateText}
              </Text>
            </Group>
            <Group gap={8} wrap="nowrap">
              <Clock size={14} color="var(--medley-text-secondary)" />
              <Text size="sm" c="dimmed">
                <Text span fw={600} c="dark">
                  {timeText}
                </Text>{' '}
                &middot; {durationMinutes} min &middot;{' '}
                {getTimezoneShortName(timezone, new Date(startTime))}
              </Text>
            </Group>
            <Group gap={8} wrap="nowrap">
              <User size={14} color="var(--medley-text-secondary)" />
              <Text size="sm" c="dimmed">
                {coachName}
              </Text>
            </Group>
          </Stack>
        </Box>

        <InviteOnItsWay email={email} fallback={fallback} />
        <ChangeOrCancel fallback={fallback} surface="portal" />

        <Button fullWidth size="md" color="medleyGreen" onClick={onDone}>
          Done
        </Button>
      </Stack>
    </Modal>
  );
}
